import { createFileRoute } from "@tanstack/react-router";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { useState } from "react";
import { PortalShell } from "@/components/portal/PortalShell";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { toast } from "sonner";
import { listEkdApprovals, decideEkdChange } from "@/lib/ekd-approval.functions";
import { supabase } from "@/integrations/supabase/client";

const NAV = [
  { to: "/app/approvals", label: "Freigaben" },
  { to: "/app/crm/projects", label: "Projekte" },
];

export const Route = createFileRoute("/_authenticated/app/approvals")({
  ssr: false,
  head: () => ({ meta: [{ title: "Freigaben" }, { name: "robots", content: "noindex,nofollow" }] }),
  component: ApprovalsPage,
});

function ApprovalsPage() {
  const qc = useQueryClient();
  const fetchEkd = useServerFn(listEkdApprovals);
  const decide = useServerFn(decideEkdChange);
  const [busy, setBusy] = useState<string | null>(null);

  const experts = useQuery({
    queryKey: ["expert-approvals"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("expert_approvals").select("*").order("requested_at", { ascending: false }).limit(200);
      if (error) throw error;
      return data ?? [];
    },
  });

  const ekd = useQuery({ queryKey: ["ekd-approvals"], queryFn: () => fetchEkd() });

  const decideMut = useMutation({
    mutationFn: async (args: { id: string; decision: "approved" | "rejected" }) => {
      setBusy(args.id);
      return await decide({ data: args });
    },
    onSuccess: () => {
      toast.success("Entscheidung gespeichert");
      qc.invalidateQueries({ queryKey: ["ekd-approvals"] });
    },
    onError: (e: Error) => toast.error(e.message),
    onSettled: () => setBusy(null),
  });

  return (
    <PortalShell nav={NAV}>
      <h1 className="text-2xl font-semibold mb-6">Freigaben</h1>
      <Tabs defaultValue="expert">
        <TabsList>
          <TabsTrigger value="expert">Expertenfreigaben</TabsTrigger>
          <TabsTrigger value="ekd">EKD-Vier-Augen</TabsTrigger>
        </TabsList>
        <TabsContent value="expert">
          <Card>
            <CardHeader><CardTitle className="text-base">Offene Expertenfreigaben</CardTitle></CardHeader>
            <CardContent>
              <Table>
                <TableHeader><TableRow><TableHead>Status</TableHead><TableHead>Typ</TableHead><TableHead>Prio</TableHead><TableHead>Erstellt</TableHead></TableRow></TableHeader>
                <TableBody>
                  {(experts.data ?? []).map((a) => (
                    <TableRow key={a.id}>
                      <TableCell><Badge variant={a.status === "pending" ? "secondary" : "outline"}>{a.status}</Badge></TableCell>
                      <TableCell className="font-mono text-xs">{a.entity_type}</TableCell>
                      <TableCell>{a.priority}</TableCell>
                      <TableCell>{new Date(a.requested_at).toLocaleString("de-DE")}</TableCell>
                    </TableRow>
                  ))}
                  {(experts.data ?? []).length === 0 && (
                    <TableRow><TableCell colSpan={4} className="text-center text-muted-foreground py-6">Keine Einträge.</TableCell></TableRow>
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>
        <TabsContent value="ekd">
          <Card>
            <CardHeader><CardTitle className="text-base">EKD-Änderungen (Vier-Augen-Prinzip)</CardTitle></CardHeader>
            <CardContent>
              <Table>
                <TableHeader><TableRow><TableHead>Status</TableHead><TableHead>Code</TableHead><TableHead>Vorgeschlagen</TableHead><TableHead>Aktionen</TableHead></TableRow></TableHeader>
                <TableBody>
                  {(ekd.data?.rows ?? []).map((r) => {
                    const payload = (r.payload ?? {}) as { code?: string; name?: string };
                    return (
                      <TableRow key={r.id}>
                        <TableCell><Badge variant={r.status === "pending" ? "secondary" : "outline"}>{r.status}</Badge></TableCell>
                        <TableCell className="font-mono text-xs">{payload.code ?? "—"} · {payload.name ?? ""}</TableCell>
                        <TableCell>{new Date(r.proposed_at).toLocaleString("de-DE")}</TableCell>
                        <TableCell className="space-x-2">
                          {r.status === "pending" && (
                            <>
                              <Button size="sm" disabled={busy === r.id} onClick={() => decideMut.mutate({ id: r.id, decision: "approved" })}>Genehmigen</Button>
                              <Button size="sm" variant="outline" disabled={busy === r.id} onClick={() => decideMut.mutate({ id: r.id, decision: "rejected" })}>Ablehnen</Button>
                            </>
                          )}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                  {(ekd.data?.rows ?? []).length === 0 && (
                    <TableRow><TableCell colSpan={4} className="text-center text-muted-foreground py-6">Keine offenen EKD-Änderungen.</TableCell></TableRow>
                  )}
                </TableBody>
              </Table>
              <p className="text-xs text-muted-foreground mt-3">
                Hinweis: Genehmigung durch dieselbe Person, die die Änderung vorgeschlagen hat, ist datenbankseitig blockiert.
              </p>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </PortalShell>
  );
}
